/** @format */

import { FC, useEffect, useState } from "react";

import { useTranslation, TFunction } from "react-i18next";
import { Root, createRoot } from "react-dom/client";
import QRCode from "qrcode";
import DropdownList from "../../atoms/dropdownButton/DropdownList";
import Toast from "../../core/toast/Toast";
import { IconName } from "../../core/icon/names";
import { addUtmParameters, openPopup } from "./socialmediaShare.helpers";

let root: Root;

export const downloadQRCode = async (value, name = "QRCode") => {
  try {
    const canvas = document.createElement("canvas");
    await QRCode.toCanvas(canvas, value, {
      width: 256,
      margin: 2,
    });

    const pngUrl = canvas
      .toDataURL("image/png")
      .replace("image/png", "image/octet-stream");
    const downloadLink = document.createElement("a");
    downloadLink.href = pngUrl;
    downloadLink.download = `${name}.png`;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  } catch (err) {
    console.error(err);
  }
};

export const copyLinkToClipboard = (path: string) => {
  navigator.clipboard.writeText(path);

  // Render TemporaryDiv at the beginning of the body
  if (document.getElementById("domToast")) return;
  const { firstChild } = document.body;
  const newDiv = document.createElement("div");
  document.body.insertBefore(newDiv, firstChild);
  root = createRoot(newDiv);
  root.render(<DomToast key={Date.now()} />);
};

function DomToast() {
  const { t } = useTranslation();
  const [show, setShow] = useState(true);

  useEffect(() => {
    const showTimeout = setTimeout(() => {
      setShow(false);
    }, 10);

    const removeTimeout = setTimeout(() => {
      const div = document.getElementById("domToast");
      if (div) {
        root.unmount();
        div.remove();
      }
    }, 5500);

    return () => {
      clearTimeout(showTimeout);
      clearTimeout(removeTimeout);
    };
  }, []);

  return (
    <div
      id="domToast"
      style={{ zIndex: 999999999999999999999 }}
    >
      <Toast
        id="copyLink"
        leadingIcon="duplicate"
        show={show}
      >
        {t("util:copied", { val: t("global:link") })}
      </Toast>
    </div>
  );
}

export const useSocialLinks = () => {
  const { t } = useTranslation();
  const customMessagePrivate = "Das könnte dich interessieren: ";
  const customMessage = "Das könnte dich interessieren: ";

  const socialLinks = ({
    path = window.location.href,
    trackLocation,
    app = "mainApp",
    filterOut,
  }: {
    path?: string;
    trackLocation: string;
    app: "mainApp" | "dashboard";
    filterOut?: "qr-code";
  }) => {
    return [
      {
        id: "whatsApp",
        leadingIcon: "logo/WhatsApp" as IconName,
        text: "WhatsApp",
        trackId: `${trackLocation}_whatsAppShareClick`,
        onClick: () => {
          const urlWithUtmParameters = addUtmParameters(
            path,
            "whatsApp",
            trackLocation,
            app
          );
          openPopup(
            `https://wa.me/?text=${encodeURIComponent(
              `${customMessagePrivate}\n\n${urlWithUtmParameters}`
            )}`
          );
        },
      },
      {
        id: "facebook",
        leadingIcon: "logo/Facebook" as IconName,
        text: "Facebook",
        trackId: `${trackLocation}_facebookShareClick`,
        onClick: () => {
          const urlWithUtmParameters = addUtmParameters(
            path,
            "facebook",
            trackLocation,
            app
          );
          openPopup(
            `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
              urlWithUtmParameters
            )}&quote=${encodeURIComponent(customMessage)}`
          );
        },
      },
      {
        id: "x-twitter",
        leadingIcon: "logo/X" as IconName,
        text: "X.com",
        trackId: `${trackLocation}_twitterShareClick`,
        onClick: () => {
          const urlWithUtmParameters = addUtmParameters(
            path,
            "twitter",
            trackLocation,
            app
          );
          openPopup(
            `http://www.twitter.com/intent/tweet?url=${encodeURIComponent(
              urlWithUtmParameters
            )}&text=${encodeURIComponent(customMessage)}`
          );
        },
      },
      {
        id: "mail",
        leadingIcon: "mail" as IconName,
        text: "Email",
        trackId: `${trackLocation}_emailShareClick`,
        onClick: () => {
          const urlWithUtmParameters = addUtmParameters(
            path,
            "email",
            trackLocation,
            app
          );
          window.open(
            `mailto:?body=${encodeURIComponent(
              `${customMessagePrivate}\n\n${urlWithUtmParameters}`
            )}`
          );
        },
      },
      {
        id: "qr-code",
        leadingIcon: "qr-code" as IconName,
        text: t("util:download", { val: "QR-Code" }),
        trackId: `${trackLocation}_qrCodeShareClick`,
        returnModalContext: true,
        onClick: () =>
          downloadQRCode(
            addUtmParameters(path, "copyLink", trackLocation, app),
            `qrCode-${path}`
          ),
      },

      {
        id: "copyLink",
        leadingIcon: "link" as IconName,
        text: t("util:copy", { val: "URL" }),
        trackId: `${trackLocation}_copyLinkShareClick`,
        returnModalContext: true,
        onClick: () => {
          copyLinkToClipboard(
            addUtmParameters(path, "copyLink", trackLocation, app)
          );
        },
      },
    ].filter(({ id }) => !filterOut?.includes(id));
  };

  return { socialLinks };
};

const SocialmediaShare: FC<{ trackLocation: string }> = ({ trackLocation }) => {
  const { socialLinks } = useSocialLinks();
  return (
    <DropdownList
      name="social-media-share"
      data={socialLinks({ trackLocation })}
    />
  );
};

export default SocialmediaShare;
